/* eslint react-hooks/exhaustive-deps: "warn" */


import cn from 'classnames';

import { Section } from 'ui/molecules/Section/Section';

import styles from './AIProductAskSection.module.scss';

type Category = 'compliance' | 'sales' | 'research';

type AskItem = {
  category: Category;
  categoryLabel: string;
  prompt: string;
  description: string;
};

const ITEMS: AskItem[] = [
  {
    category: 'compliance',
    categoryLabel: 'Compliance',
    prompt: 'Verify the company with VAT number DE814468598. Is it active?',
    description:
      'Verify any business by name, registration number, VAT, or EIN. Get status, incorporation date, and registered address from the official registry.',
  },
  {
    category: 'compliance',
    categoryLabel: 'Compliance',
    prompt: 'Who owns Deliveroo? Show me the full ownership chain up to the UBO.',
    description:
      'Trace ultimate beneficial owners through complex corporate structures. See every link in the chain from shareholders to the person at the top.',
  },
  {
    category: 'compliance',
    categoryLabel: 'Compliance',
    prompt: 'Map the full group structure of Unilever PLC including all subsidiaries.',
    description:
      'Visualise parent-subsidiary relationships across jurisdictions. See every entity, their country of incorporation, and ownership percentages.',
  },
  {
    category: 'sales',
    categoryLabel: 'Sales',
    prompt: 'Find SaaS companies in the Nordics with 50–200 employees and €10M+ revenue.',
    description:
      'Build hyper-targeted prospect lists by location, revenue, headcount, SIC code, and more — using real filed data, not estimates.',
  },
  {
    category: 'sales',
    categoryLabel: 'Sales',
    prompt: 'Find UK construction firms growing over 30% YoY for the last 2 years.',
    description:
      'Spot high-growth companies before your competitors. Regis calculates growth from actual filed financial statements, not self-reported data.',
  },
  {
    category: 'sales',
    categoryLabel: 'Sales',
    prompt: 'Find companies in Poland owned by German entities with revenue above €20M.',
    description:
      'Filter by foreign ownership, country of parent company, EBITDA, net profit margin — any financial or corporate criteria you need.',
  },
  {
    category: 'research',
    categoryLabel: 'Research',
    prompt: "Show me Bosch GmbH's financials for the last 5 years side by side.",
    description:
      'Access up to 20 years of financial statements for private and public companies. Revenue, profit, EBITDA, assets, liabilities — from official filings.',
  },
  {
    category: 'research',
    categoryLabel: 'Research',
    prompt: 'Find Italian manufacturers with EBITDA above €5M and under 500 employees.',
    description:
      'Target companies by any financial metric. Build lists of financially qualified targets — not just companies with a website and a logo.',
  },
  {
    category: 'research',
    categoryLabel: 'Research',
    prompt: 'List the top 20 fastest growing tech companies in Spain by revenue.',
    description:
      'Run complex market research queries in seconds. Rank companies by any metric across any geography. Export results to CSV instantly.',
  },
];

export const AIProductAskSection = () => {
  return (
    <Section
      fullWidth
      introBlockClassName={styles.intro}
      title="Just type a question. Like you would in ChatGPT."
      description="Regis understands natural language. No filters. No query builders. No learning curve. Here are some things people ask every day."
      className={styles.section}
    >
      <div className={styles.grid}>
        {ITEMS.map((item, idx) => (
          <div key={idx} className={cn(styles.card, styles[`card_${item.category}`])}>
            <div className={cn(styles.category, styles[`category_${item.category}`])}>{item.categoryLabel}</div>

            <div className={styles.promptRow}>
              <span className={styles.promptPrefix} aria-hidden="true">
                &gt;
              </span>
              <div className={styles.prompt}>
                {item.prompt}
                <span className={styles.cursor} aria-hidden="true" />
              </div>
              <span className={styles.runIcon} aria-hidden="true">
                <svg width="11" height="11" viewBox="0 0 10 10" fill="none">
                  <path
                    d="M2 8L8 2M8 2H3M8 2V7"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>

            <div className={styles.description}>{item.description}</div>
          </div>
        ))}
      </div>
    </Section>
  );
};
